"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { AdminItemDto, ItemDto, ItemStatus } from "@rinastyling/common";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import useInput from "../hooks/useInput";
import { usePopover } from "../hooks/usePopover";

import dynamic from "next/dynamic";
import { DeleteIcon } from "../icons/DeleteIcon";
import { isMobileAtom } from "../store/layoutStore";
import useAdminItems from "../store/useAdminItems";
import useCart from "../store/useCart";
import { userAtom } from "../store/userAtom";
import {
  CardWrap,
  FlexCoulmn,
  FlexRow,
  Fonts,
  NextStyledImage
} from "../styles/globalsStyles";
import { Routes } from "../utils/clientRoutes";
import AreYouSurePrompt from "./AreYouSurePrompt";
import Carousel from "./Carousel";
import { useRouter } from "next/navigation";
const SoldOutTxt = dynamic(() => import("./SoldOutTxt"));
const AddToCartBtn = dynamic(() => import("./AddToCartBtn"));
const ItemForm = dynamic(() => import("./AdminForms/ItemForm"));
const EditIcon = dynamic(() => import("../icons/EditIcon"));

interface ItemCardProps {
  item: AdminItemDto | ItemDto;
  isAdminPage?: boolean;
  onAdminMark?: (itemId: string, isMarked: boolean) => void;
  isSingleItemPage?: boolean;
}
const ItemCard = ({
  item,
  isAdminPage,
  onAdminMark,
  isSingleItemPage
}: ItemCardProps) => {
  const router = useRouter();
  const user = useAtomValue(userAtom);
  const groupSign = useInput({
    initalValue: false,
    type: "checkbox",
    maxWidth: true
  });
  const isMobile = useAtomValue(isMobileAtom);
  useEffect(() => {
    if (!user?.isAdmin || !onAdminMark) return;
    onAdminMark(item.id, groupSign.value);
  }, [groupSign.value, item.id, onAdminMark, user?.isAdmin]);

  const onItemClicked = useCallback(() => {
    if (isSingleItemPage) return;

    router.push(Routes.popupStoreItem.replace(":itemId", item.id));
  }, [isSingleItemPage, item.id, router]);

  const { cart } = useCart();
  const isItemAvialbleOrInMyCart = useMemo(
    () =>
      item.status === ItemStatus.available ||
      !!(
        item.status === ItemStatus.inCart &&
        cart?.items.find((item) => item.id === item.id)
      ),
    [cart?.items, item.status]
  );
  const itemCardJsx = useMemo(
    () => (
      <CardWrap
        $width={isSingleItemPage ? "100%" : "calc(50% - .5rem)"}
        // $width={"calc(50% - .5rem)"}
        $height={""}
        onClick={onItemClicked}
        style={{
          cursor: "pointer",
          opacity: isItemAvialbleOrInMyCart ? "1" : ".6"
        }}
      >
        <FlexCoulmn $alignItems="center" $gap={".5rem"} $width={"100%"}>
          <FlexRow
            $width={"100%"}
            $justifyContent="center"
            $gap={"1rem"}
            $alignItems="center"
          >
            <ItemNameTitle>{item.name}</ItemNameTitle>
          </FlexRow>

          <Carousel>
            {item.imgs.map((img, i) => (
              <NextStyledImage
                src={img}
                key={item.id + "__" + i}
                width={isMobile ? 150 : 370}
                height={isMobile ? 180 : 400}
                $objectFit="cover"
                alt={item.name}
                loading="lazy"
              />
            ))}
          </Carousel>
          <h3>{item.price}₪</h3>
          {isSingleItemPage && (
            <ItemDescription>{item.description}</ItemDescription>
          )}
          {!isAdminPage && isItemAvialbleOrInMyCart && (
            <AddToCartBtn itemId={item.id} />
          )}
          {!isItemAvialbleOrInMyCart && <SoldOutTxt />}
        </FlexCoulmn>
      </CardWrap>
    ),
    [
      isAdminPage,
      isItemAvialbleOrInMyCart,
      isMobile,
      isSingleItemPage,
      item.description,
      item.id,
      item.imgs,
      item.name,
      item.price,
      onItemClicked
    ]
  );
  if (user?.isAdmin && isAdminPage)
    return (
      <FlexCoulmn $gap={".5rem"}>
        {user?.isAdmin && isAdminPage && (
          <span style={{ width: "25px" }}>{groupSign.jsx}</span>
        )}
        {itemCardJsx}
        {user?.isAdmin && isAdminPage && <ItemCardAdmin item={item as any} />}
      </FlexCoulmn>
    );
  if (!isAdminPage) return itemCardJsx;
};

interface ItemCardAdminProps {
  item: AdminItemDto;
}
const ItemCardAdmin = ({ item }: ItemCardAdminProps) => {
  const [deleteForSure, setDeleteForSure] = useState(false);
  const { handleDeleteItem } = useAdminItems();
  const { showPopover } = usePopover();
  const handleEditIconClick = useCallback(() => {
    showPopover(<ItemForm editItem={item} />, { cancelPressOutside: true });
  }, [item, showPopover]);

  const handleDeleteIconClick = useCallback(() => {
    showPopover(
      <AreYouSurePrompt setAnswer={setDeleteForSure} notInPopover>
        למחוק בדוק?
      </AreYouSurePrompt>
    );
  }, [showPopover]);

  useEffect(() => {
    if (deleteForSure) {
      handleDeleteItem({ itemId: item.id });
    }
  }, [deleteForSure, handleDeleteItem, item.id]);

  return (
    <FlexRow $gap={"1rem"} $alignItems="center">
      <EditIcon onClick={handleEditIconClick} width={40} height={40} />
      {item.status === ItemStatus.available && (
        <>
          <DeleteIcon width={40} height={40} onClick={handleDeleteIconClick} />
        </>
      )}
    </FlexRow>
  );
};

const ItemNameTitle = styled.h1`
  text-align: center;
  ${Fonts.medium}
  white-space: nowrap;
`;
const ItemDescription = styled.p`
  text-align: center;
  ${Fonts.small}
`;

export default ItemCard;
